<template>
  <div class="bitts-range-picker">
    <RangePicker
      v-if="!useSingleDatePicker"
      :id="id"
      separator="-"
      :allow-clear="false"
      :value="dateRange"
      :format="format"
      :disabled-date="disabledDate"
      :disabled="disabled"
      @change="onRangeChange"
    >
      <template #suffixIcon>
        <div>
          <FontAwesomeIcon
            :icon="['fak', 'calendar']"
            :style="{ height: '16px', width: '16px', color: 'currentColor' }"
            class="text-neutral-text-placeholder mr-4"
          />
        </div>
      </template>
    </RangePicker>
    <DatePicker
      v-else
      v-bind="dynamicProps"
      :id="id"
      :allow-clear="false"
      :autofocus="autoFocus"
      :value="date"
      :format="format"
      :disabled-date="disabledDate"
      :disabled="disabled"
      :placeholder="placeholder"
      @change="onDateChange"
    >
      <template #suffixIcon>
        <div>
          <FontAwesomeIcon
            :icon="['far', 'calendar']"
            :style="{ height: '16px', width: '16px', color: 'currentColor' }"
            class="text-neutral-text-placeholder mr-4"
          />
        </div>
      </template>
    </DatePicker>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DatePicker, RangePicker } from 'ant-design-vue';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { computed, ref, watch } from 'vue';

type DateInput = string | Dayjs;
type DateValue = string | Dayjs;
type RangeValue = [string, string] | [Dayjs, Dayjs];

export type BittsRangePickerChangeValue = Dayjs | [Dayjs, Dayjs];

const {
  autoFocus = false,
  disabledDate = () => false,
  format = 'YYYY/MM/DD',
  disabled = false,
  placeholder = 'Select a date',
  initialDate = '',
  initialDateRange = [],
  useOpen = false,
  isOpen = false,
  useSingleDatePicker = false,
  id = undefined,
} = defineProps<{
  autoFocus?: boolean;
  disabledDate?: (current: Dayjs) => boolean;
  format?: string;
  disabled?: boolean;
  placeholder?: string;
  initialDate?: DateInput;
  initialDateRange?: DateInput[];
  useOpen?: boolean;
  isOpen?: boolean;
  useSingleDatePicker?: boolean;
  id?: string;
}>();

const emit = defineEmits<{
  change: [dateInfo: BittsRangePickerChangeValue];
}>();

const date = ref<DateValue | undefined>(
  initialDate ? dayjs(initialDate, format) : undefined,
);
const dateRange = ref<RangeValue | undefined>(
  initialDateRange.length === 2
    ? [dayjs(initialDateRange[0], format), dayjs(initialDateRange[1], format)]
    : undefined,
);

watch(
  () => initialDate,
  (newInitialDate) => {
    date.value = newInitialDate ? dayjs(newInitialDate, format) : undefined;
  },
);

const dynamicProps = computed(() => (useOpen ? { open: isOpen } : {}));

function onDateChange(dateInfo: DateValue) {
  date.value = dateInfo;
  const normalized =
    typeof dateInfo === 'string' ? dayjs(dateInfo, format) : dateInfo;
  emit('change', normalized);
}

function onRangeChange(dateInfo: RangeValue) {
  dateRange.value = dateInfo;
  const [start, end] = dateInfo;
  const normalized: [Dayjs, Dayjs] = [
    typeof start === 'string' ? dayjs(start, format) : start,
    typeof end === 'string' ? dayjs(end, format) : end,
  ];
  emit('change', normalized);
}
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.ant-picker-dropdown {
  font-family: inherit;
}
.bitts-range-picker {
  .ant-picker {
    font-family: inherit;
    @apply border-neutral-border;
  }
  .ant-calendar-picker-input {
    height: 40px;
    width: 240px;
    @apply border-2 border-neutral-background-disabled text-neutral-800 bg-white;
  }
  .ant-input-disabled {
    @apply bg-neutral-100;
  }
  .ant-calendar-range-picker-input {
    width: 40%;
  }
  .ant-calendar-range-picker-separator {
    vertical-align: unset;
  }
  .ant-calendar-picker-icon {
    top: 45%;
  }
  .ant-picker:hover {
    @apply border-neutral-border-focus outline-neutral-border-focus outline;
  }
  .ant-picker-focused {
    @apply border-neutral-border-focus outline-neutral-border-focus outline;
  }
  .ant-picker-input input::placeholder {
    @apply text-neutral-text-placeholder;
  }
}
</style>
